/**
 * >>> **HER FILTER ENGINE: THE OUTGOING FEED, PROCESSED ON HER OWN DEVICE.** <<<
 *
 * Her ruling: *"the filtered feed IS the broadcast feed (audience sees the filtered her)."* So this
 * does not decorate a preview — it takes the camera stream, runs it through a shader, and hands back
 * a NEW MediaStream whose video track is the canvas. That track is what gets published.
 *
 * **HER VOICE RIDES UNTOUCHED.** The audio is the camera's own track, passed straight through: a
 * filter has no business near her microphone, and re-wrapping audio is exactly how a pipeline ends
 * up silencing a broadcast it was only meant to prettify.
 *
 * ================================================================================================
 * >>> **HER MIRROR TEST REJECTED THE FIRST ENGINE, AND IT WAS RIGHT FOR A REASON THAT IS
 *     ARITHMETIC RATHER THAN TASTE. THIS FILE IS THE REPAIR.** <<<
 *
 * The first engine smoothed with **nine taps at +-2 pixels** — a five-pixel neighbourhood. A blemish
 * on a face filling a portrait phone frame is **fifteen to fifty pixels across**. That kernel could
 * never touch one: it averaged sensor noise and left every mark exactly where it was. What her eye
 * COULD see was `warm` and `brighten`, which were full-frame with large coefficients. So the whole
 * thing read as a colour wash, which is her verdict word for word.
 *
 * **A BIGGER NUMBER ON THAT SHADER WOULD NOT HAVE FIXED IT.** Widening a single-pass bilateral to a
 * blemish-sized radius means hundreds of taps per pixel per frame, on a phone that is already
 * running a video encoder. The algorithm had to change, not the constant.
 *
 * >>> **WHAT IT DOES NOW: FREQUENCY SEPARATION — THE THING BEAUTY CAMERAS ACTUALLY DO.** <<<
 *
 *   1. **A wide blur, computed at ONE-EIGHTH RESOLUTION** into an offscreen buffer. Forty-nine taps
 *      there reach a **72-pixel neighbourhood** at full res for about 1% of the cost of doing it at
 *      full size. This is the *base* layer: skin with every blemish, pore and blotch averaged away.
 *      **THE REACH HAD TO BE MUCH LARGER THAN THE MARK, AND THAT WAS MEASURED RATHER THAN GUESSED**
 *      — a kernel the same width as a blemish only halves it, which is what the first attempt at
 *      this file did (48% of the mark still standing). `scripts/live-filter-strength.mjs` is where
 *      that number comes from and where it is kept honest.
 *   2. **The DETAIL layer is what the blur removed** — `original - base`. Every mark AND every
 *      eyelash lives in it, and the two are told apart by **AMPLITUDE**: skin texture and blemishes
 *      are low-contrast, lashes, lid lines, nostrils and lip edges are high-contrast.
 *   3. **The composite keeps detail in proportion to how strong it is.** Flat skin falls to the base
 *      layer and the blemish is gone; a lash keeps its full detail and stays razor sharp.
 *
 * **THAT IS HER "NEVER VASELINE" CLAUSE, AND IT IS STRUCTURAL RATHER THAN A TUNING.** A plain blur
 * destroys lashes because it cannot tell them from a blemish. This one is told by amplitude, so the
 * smoothing can go all the way to blemish-covering strength with the eyes still sharp.
 *
 * **THE SECOND PASS IS PAID FOR AND THE OLD COMMENT SAYING IT COULD NOT BE IS WITHDRAWN.** The
 * previous file argued a second framebuffer was "a dropped frame her audience can see". It is an
 * eighth-resolution pass — about 1.5% of the pixels of the main one — and it is the only reason the
 * smoothing exists at all. An engine that holds 30fps and does nothing is not the cheaper option.
 *
 * **AND EVERY FAILURE ARM HANDS BACK THE BARE CAMERA, NEVER A BLACK SCREEN (R19).** No WebGL, a
 * shader that will not compile, a framebuffer that will not bind, a canvas that cannot capture: all
 * answer `null`, and the caller publishes the stream it already had. Her clause is *"never a broken
 * or frozen camera"*, and the only way to honour that is for every failure to return the thing that
 * was already working.
 */
export type FilterKnobs = { smooth: number; brighten: number; warm: number; bloom: number };

type FaceMaskLike = {
  cx: number; cy: number; rx: number; ry: number;
  eyeL: [number, number, number];
  eyeR: [number, number, number];
  mouth: [number, number, number];
};

/** Which tier is actually carrying the picture right now. Her re-test needs to SEE this. */
export type FilterTier = "face" | "full-frame";

export type FilterPipeline = {
  /** The stream to publish. The SAME object across preset changes, so a live sender is never re-negotiated. */
  stream: MediaStream;
  /** Change the look mid-broadcast, with no new track and no re-publish. */
  setKnobs: (knobs: FilterKnobs) => void;
  /** Point the pipeline at a new camera (her flip), keeping the same outgoing track. */
  setSource: (source: MediaStream) => void;
  /** The face-aware tier, attached once it has loaded. Never required: `null` is full-frame. */
  attachTracker: (t: { read: () => FaceMaskLike | null; stop: () => void } | null) => void;
  /**
   * >>> **WHICH TIER DREW THE LAST FRAME — READ, NEVER ASSUMED.** <<< Her question after the mirror
   * test was *"was I getting the fallback instead of the face tier?"*, and nothing on the screen
   * could answer it. This answers it from the draw loop itself rather than from whether a tracker
   * object exists: a tracker that is attached but returns no mask IS the full-frame tier.
   */
  tier: () => FilterTier;
  /** The element the tracker watches — the SAME one the shader samples, so they cannot disagree. */
  sourceEl: HTMLVideoElement;
  stop: () => void;
};

const VERT = [
  "attribute vec2 p;",
  "varying vec2 uv;",
  "void main(){ uv = vec2((p.x + 1.0) / 2.0, 1.0 - (p.y + 1.0) / 2.0); gl_Position = vec4(p, 0.0, 1.0); }",
].join("\n");

// ---- PASS 1: THE BASE LAYER ------------------------------------------------------------------
// A wide blur at one-eighth resolution. The colour weight is DELIBERATELY LOOSE (`* 8.0`, against the
// composite's much tighter test): this pass is meant to average a blemish away, so it must blur
// straight across one. It resists only a strong boundary — the edge of a face against a background
// — which is what stops a halo of wall colour landing on her cheek.
const BLUR_FRAG = [
  "precision mediump float;",
  "varying vec2 uv;",
  "uniform sampler2D tex;",
  "uniform vec2 step;",
  "void main(){",
  "  vec3 c = texture2D(tex, uv).rgb;",
  "  vec3 sum = vec3(0.0); float wsum = 0.0;",
  "  for (int i = -3; i <= 3; i++) {",
  "    for (int j = -3; j <= 3; j++) {",
  "      vec3 s = texture2D(tex, uv + vec2(float(i), float(j)) * step).rgb;",
  "      float d = distance(s, c);",
  "      float w = exp(-d * d * 8.0);",
  "      sum += s * w; wsum += w;",
  "    }",
  "  }",
  "  gl_FragColor = vec4(sum / max(wsum, 0.0001), 1.0);",
  "}",
].join("\n");

// ---- PASS 2: THE COMPOSITE -------------------------------------------------------------------
const FRAG = [
  "precision mediump float;",
  "varying vec2 uv;",
  "uniform sampler2D tex;",     // the camera frame, full resolution
  "uniform sampler2D baseTex;", // pass 1's blurred base, eighth resolution, bilinear upsampled
  "uniform float smoothAmt, brightenAmt, warmAmt, bloomAmt;",
  // >>> **HER FACE-AWARE TIER, IN FOUR UNIFORMS.** <<< `faceR.x <= 0.0` means NO FACE, and that is
  // not an error state -- it is the full-frame tier, which her own tiering clause names as the
  // fallback. One shader, two tiers, no second program to keep in step.
  "uniform vec2 faceC, faceR;",
  "uniform vec3 eyeL, eyeR, mouth;",
  "void main(){",
  "  vec3 c = texture2D(tex, uv).rgb;",
  // HOW MUCH SMOOTHING THIS PIXEL GETS. Full-frame when there is no mask; inside the oval and away
  // from eyes and lips when there is. The falloffs are soft on purpose: a hard edge on a beauty
  // filter reads as a mask sitting on somebody's face, which is the one thing it must never look like.
  "  float amt = smoothAmt;",
  "  if (faceR.x > 0.0) {",
  "    vec2 d = (uv - faceC) / max(faceR, vec2(0.0001));",
  "    float inFace = 1.0 - smoothstep(0.82, 1.06, length(d));",
  "    float keepL = smoothstep(eyeL.z * 0.72, eyeL.z * 1.5, distance(uv, eyeL.xy));",
  "    float keepR = smoothstep(eyeR.z * 0.72, eyeR.z * 1.5, distance(uv, eyeR.xy));",
  "    float keepM = smoothstep(mouth.z * 0.72, mouth.z * 1.5, distance(uv, mouth.xy));",
  "    amt = smoothAmt * inFace * min(min(keepL, keepR), keepM);",
  "  }",
  "  if (amt > 0.001) {",
  // THE BASE LAYER IS STORED VERTICALLY FLIPPED and that is not a bug to chase later: the vertex
  // shader flips Y so the camera texture reads right-way-up on screen, and applying that same flip
  // while rendering INTO a framebuffer stores the result upside down. Reading it back with the
  // inverse is one line here, against a second vertex shader that would have to be kept in step.
  "    vec3 base = texture2D(baseTex, vec2(uv.x, 1.0 - uv.y)).rgb;",
  // >>> **THE WHOLE FILTER IS THESE FOUR LINES.** <<< What the blur removed is the detail layer;
  // how STRONG that detail is, is what tells a blemish from an eyelash. Low amplitude is skin and
  // falls away to the base; high amplitude is a real feature and is kept whole.
  "    vec3 detail = c - base;",
  "    float mag = max(max(abs(detail.r), abs(detail.g)), abs(detail.b));",
  "    float keep = smoothstep(0.07, 0.24, mag);",
  "    c = base + detail * mix(1.0, keep, amt);",
  "  }",
  // ---- BRIGHTEN, WEIGHTED TO THE MIDTONES ----------------------------------------------------
  // A flat add blows the highlights out; this lifts where there is room to lift.
  "  float l = dot(c, vec3(0.299, 0.587, 0.114));",
  "  c += brightenAmt * (1.0 - l) * 0.9;",
  // ---- WARM: TOWARD GOLD, NEVER TOWARD MAGENTA (THE COLOUR LAW, in a shader) ------------------
  "  c.r += warmAmt * 0.100;",
  "  c.g += warmAmt * 0.045;",
  "  c.b -= warmAmt * 0.055;",
  // ---- BLOOM: a soft spill from the brightest parts only, so it reads as light and not as haze --
  "  if (bloomAmt > 0.001) {",
  "    float hi = max(0.0, l - 0.62) * 2.6;",
  "    c += bloomAmt * hi * vec3(1.0, 0.94, 0.82) * 0.5;",
  "  }",
  "  gl_FragColor = vec4(clamp(c, 0.0, 1.0), 1.0);",
  "}",
].join("\n");

function compile(gl: WebGLRenderingContext, type: number, src: string) {
  const sh = gl.createShader(type);
  if (!sh) return null;
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  return gl.getShaderParameter(sh, gl.COMPILE_STATUS) ? sh : null;
}

function link(gl: WebGLRenderingContext, fragSrc: string) {
  const vs = compile(gl, gl.VERTEX_SHADER, VERT);
  const fs = compile(gl, gl.FRAGMENT_SHADER, fragSrc);
  if (!vs || !fs) return null;
  const prog = gl.createProgram();
  if (!prog) return null;
  gl.attachShader(prog, vs);
  gl.attachShader(prog, fs);
  gl.linkProgram(prog);
  return gl.getProgramParameter(prog, gl.LINK_STATUS) ? prog : null;
}

/** The divisor for the base layer's resolution. Named once so the reach is readable. */
const BASE_DIV = 8;

/**
 * Build the pipeline, or hand back nothing and let the caller publish the bare camera.
 *
 * `null` is a real answer rather than an error: it is her OFF state and every failure arm at once.
 * There is no half-built pipeline in this function's vocabulary.
 */
export function startFilterPipeline(source: MediaStream, knobs: FilterKnobs): FilterPipeline | null {
  if (typeof document === "undefined") return null;
  const track = source.getVideoTracks()[0];
  if (!track) return null;

  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  video.srcObject = new MediaStream([track]);
  void video.play().catch(() => {});

  const settings = (track.getSettings && track.getSettings()) || {};
  const canvas = document.createElement("canvas");
  canvas.width = Number(settings.width) || 720;
  canvas.height = Number(settings.height) || 1280;

  const gl = (canvas.getContext("webgl", { alpha: false })
    || canvas.getContext("experimental-webgl")) as WebGLRenderingContext | null;
  if (!gl) return null;

  const blurProg = link(gl, BLUR_FRAG);
  const mainProg = link(gl, FRAG);
  if (!blurProg || !mainProg) return null;

  // ONE fullscreen triangle, shared by both passes. The attribute location is asked per program
  // rather than assumed equal — two programs are two link results and nothing promises they match.
  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
  const bindGeom = (prog: WebGLProgram) => {
    const loc = gl.getAttribLocation(prog, "p");
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
  };

  const makeTex = () => {
    const t = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, t);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    return t;
  };

  const tex = makeTex();        // the camera frame
  const baseTex = makeTex();    // pass 1's output, eighth resolution
  const fbo = gl.createFramebuffer();
  if (!tex || !baseTex || !fbo) return null;

  let baseW = 0;
  let baseH = 0;
  // The base buffer follows the camera's real size, re-allocated only when that changes.
  const sizeBase = (w: number, h: number) => {
    const nw = Math.max(1, Math.floor(w / BASE_DIV));
    const nh = Math.max(1, Math.floor(h / BASE_DIV));
    if (nw === baseW && nh === baseH) return true;
    gl.bindTexture(gl.TEXTURE_2D, baseTex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, nw, nh, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, baseTex, 0);
    const ok = gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE;
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    if (!ok) return false;
    baseW = nw; baseH = nh;
    return true;
  };
  if (!sizeBase(canvas.width, canvas.height)) return null;

  const uBlur = {
    step: gl.getUniformLocation(blurProg, "step"),
    tex: gl.getUniformLocation(blurProg, "tex"),
  };
  const u = {
    smooth: gl.getUniformLocation(mainProg, "smoothAmt"),
    brighten: gl.getUniformLocation(mainProg, "brightenAmt"),
    warm: gl.getUniformLocation(mainProg, "warmAmt"),
    bloom: gl.getUniformLocation(mainProg, "bloomAmt"),
    faceC: gl.getUniformLocation(mainProg, "faceC"),
    faceR: gl.getUniformLocation(mainProg, "faceR"),
    eyeL: gl.getUniformLocation(mainProg, "eyeL"),
    eyeR: gl.getUniformLocation(mainProg, "eyeR"),
    mouth: gl.getUniformLocation(mainProg, "mouth"),
    tex: gl.getUniformLocation(mainProg, "tex"),
    baseTex: gl.getUniformLocation(mainProg, "baseTex"),
  };

  // The tracker is attached later and may never arrive; `null` is the full-frame tier.
  let tracker: { read: () => FaceMaskLike | null; stop: () => void } | null = null;
  let current = knobs;
  let live = true;
  let raf = 0;
  // WHAT THE LAST FRAME ACTUALLY DID, not what was configured. A tracker that is attached but
  // finds no face is the full-frame tier, and saying otherwise would answer her question wrongly.
  let lastTier: FilterTier = "full-frame";

  const draw = () => {
    if (!live) return;
    raf = requestAnimationFrame(draw);
    if (video.readyState < 2 || !video.videoWidth) return;
    if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      if (!sizeBase(canvas.width, canvas.height)) { live = false; return; }
    }

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, tex);
    try {
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, video);
    } catch {
      return;   // a frame that will not upload is a frame skipped, never a broadcast dropped
    }

    // ---- PASS 1: the base layer, into the eighth-res buffer ---------------------------------
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.viewport(0, 0, baseW, baseH);
    gl.useProgram(blurProg);
    bindGeom(blurProg);
    gl.uniform1i(uBlur.tex, 0);
    // The step is in BASE pixels, so +-3 taps at 1.5 spacing reach +-4.5 base pixels = **+-36
    // full-res pixels**, a 72px square. A blemish is 15-50px across, so the kernel is comfortably
    // larger than the thing it is averaging away — which is the property that makes it work, and
    // the one the old +-2px kernel could not have had at any strength.
    gl.uniform2f(uBlur.step, 1.5 / baseW, 1.5 / baseH);
    gl.drawArrays(gl.TRIANGLES, 0, 3);

    // ---- PASS 2: the composite, to the canvas ------------------------------------------------
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.useProgram(mainProg);
    bindGeom(mainProg);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.uniform1i(u.tex, 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, baseTex);
    gl.uniform1i(u.baseTex, 1);
    gl.uniform1f(u.smooth, current.smooth);
    gl.uniform1f(u.brighten, current.brighten);
    gl.uniform1f(u.warm, current.warm);
    gl.uniform1f(u.bloom, current.bloom);
    // FACE-AWARE WHERE THERE IS A FACE, FULL-FRAME WHERE THERE IS NOT -- decided every frame, so a
    // member stepping out of shot is carried by the tier below rather than by a stale mask.
    const m = tracker?.read?.() || null;
    if (m) {
      gl.uniform2f(u.faceC, m.cx, m.cy);
      gl.uniform2f(u.faceR, m.rx, m.ry);
      gl.uniform3f(u.eyeL, m.eyeL[0], m.eyeL[1], m.eyeL[2]);
      gl.uniform3f(u.eyeR, m.eyeR[0], m.eyeR[1], m.eyeR[2]);
      gl.uniform3f(u.mouth, m.mouth[0], m.mouth[1], m.mouth[2]);
      lastTier = "face";
    } else {
      gl.uniform2f(u.faceR, 0, 0);
      lastTier = "full-frame";
    }
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  };
  raf = requestAnimationFrame(draw);

  const capture = (canvas as HTMLCanvasElement & { captureStream?: (fps: number) => MediaStream }).captureStream;
  const captured = typeof capture === "function" ? capture.call(canvas, 30) : null;
  if (!captured || !captured.getVideoTracks()[0]) {
    live = false;
    cancelAnimationFrame(raf);
    return null;
  }

  const out = new MediaStream([captured.getVideoTracks()[0], ...source.getAudioTracks()]);

  return {
    stream: out,
    setKnobs: (k) => { current = k; },
    attachTracker: (t) => { tracker = t; },
    tier: () => lastTier,
    sourceEl: video,
    // HER FLIP, WITHOUT A RE-PUBLISH: the pipeline swallows the new camera and keeps handing out the
    // same canvas track, so the sender never learns anything happened.
    setSource: (next) => {
      const t = next.getVideoTracks()[0];
      if (t) video.srcObject = new MediaStream([t]);
      void video.play().catch(() => {});
    },
    stop: () => {
      live = false;
      cancelAnimationFrame(raf);
      tracker?.stop?.();
      tracker = null;
      captured.getTracks().forEach((t) => t.stop());
      video.srcObject = null;
    },
  };
}
